import type { WorkflowStep } from '../../hooks/useAnalysisJob';

const REACHED = new Set(['running', 'failed', 'error']);

/**
 * Normalise workflow steps for a job that has reached a terminal state.
 *
 * The analysis runs as a parent workflow wrapping an Iris child: the parent
 * emits a coarse "Analyzing Repository" step and, on a child failure, can only
 * attribute the error to that wrapper — which sits mid-list, above the granular
 * child steps the child already streamed (e.g. a completed "Scanning Repository"
 * and a still-"running" "Storing Knowledge Graph"). The result reads as a
 * failure with steps both above and below it, and the running leaf spins forever.
 *
 * The leaf actually in-flight when execution died is the true failure point, so
 * for a failed/cancelled run we mark the highest-ordered reached step failed and
 * settle every other still-running (or coarsely-flagged) step to completed. That
 * puts the single red marker at the bottom with nothing left spinning, whether
 * the state arrives over the socket or is reloaded from the API.
 */
export function reconcileTerminalSteps(
  steps: WorkflowStep[],
  status: string,
  errorMessage?: string | null,
): WorkflowStep[] {
  if (status === 'completed') {
    if (!steps.some((s) => (s.status ?? 'pending') === 'running')) return steps;
    return steps.map((s) => (s.status === 'running' ? { ...s, status: 'completed' } : s));
  }

  if (status !== 'failed' && status !== 'cancelled') return steps;

  const reached = steps.filter((s) => REACHED.has(s.status ?? ''));
  if (reached.length === 0) return steps;

  const target = reached.reduce((a, b) => ((b.order ?? 0) >= (a.order ?? 0) ? b : a));

  return steps.map((s) => {
    if (s.id === target.id) {
      return { ...s, status: 'failed', error: s.error ?? errorMessage ?? null };
    }
    if (REACHED.has(s.status ?? '')) return { ...s, status: 'completed', error: null };
    return s;
  });
}
